// src/app/modules/social/services/player-visibility.service.ts
import { Injectable } from '@angular/core';
import { Viewport } from 'pixi-viewport';
import { Player } from '../social-pixi-canvas/player';
import { throttle } from 'lodash';
import { Loggable } from 'app/utils/logging/loggable';

@Injectable({
  providedIn: 'root',
})
export class PlayerVisibilityService extends Loggable {
  private checkInterval = 100; // Default check interval in ms
  private padding = 200; // Padding to consider player visibility

  public constructor() {
    super();
    this.setDisplayName('PlayerVisibilityService');
  }

  // Debounced function that will be configured when initialized
  public processInvisiblePlayers = throttle(
    (params: { participants: Map<string, Player>; viewport: Viewport; healthCheckFn: (player: Player) => void }) => {
      this.removeInvisiblePlayers(params);
    },
    this.checkInterval,
  );

  // Method to set the check interval - currently unused
  public setCheckInterval(): void {
    this.processInvisiblePlayers = throttle(
      (params: { participants: Map<string, Player>; viewport: Viewport; healthCheckFn: (player: Player) => void }) => {
        this.removeInvisiblePlayers(params);
      },
      this.checkInterval,
    );
  }

  private removeInvisiblePlayers({
    healthCheckFn,
    participants,
    viewport,
  }: {
    participants: Map<string, Player>;
    viewport: Viewport;
    healthCheckFn: (player: Player) => void;
  }): void {
    Array.from(participants.values()).forEach((player) => {
      if (player.wrapperContainer && !this.isPlayerVisible(player, viewport)) {
        player.visible = false;
        player.destroyUser();
      } else if (!player.wrapperContainer && this.isPlayerVisible(player, viewport)) {
        player.visible = true;
        healthCheckFn(player);
      }
    });
  }

  public isPlayerVisible(player: Player, viewport: Viewport): boolean {
    const bounds = viewport.getVisibleBounds();
    return (
      player.x + player.width > bounds.left - this.padding &&
      player.x < bounds.right &&
      player.y + player.height > bounds.top - this.padding &&
      player.y < bounds.bottom
    );
  }

  public healthCheckAll({
    healthCheckFn,
    participants,
    player,
  }: {
    player?: Player;
    participants: Map<string, Player>;
    healthCheckFn: (player: Player) => void;
  }): void {
    if (player) {
      healthCheckFn(player);
    }

    Array.from(participants.values())
      .filter((p) => p.visible)
      .forEach((p) => healthCheckFn(p));
  }
}
